import { useEffect, useRef, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Generate or get persistent visitor ID
const getVisitorId = () => {
  let visitorId = localStorage.getItem('adlinka_visitor_id');
  if (!visitorId) {
    visitorId = 'v_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    localStorage.setItem('adlinka_visitor_id', visitorId);
  }
  return visitorId;
};

// Generate session ID (new each browser session)
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('adlinka_session_id');
  if (!sessionId) {
    sessionId = 's_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
    sessionStorage.setItem('adlinka_session_id', sessionId);
  }
  return sessionId;
};

// Get referrer source
const getReferrerSource = () => {
  const referrer = document.referrer;
  if (!referrer) return 'Direct';
  
  try {
    const url = new URL(referrer);
    const hostname = url.hostname.toLowerCase();
    
    if (hostname.includes('google')) return 'Google';
    if (hostname.includes('facebook') || hostname.includes('fb.com')) return 'Facebook';
    if (hostname.includes('twitter') || hostname.includes('t.co') || hostname.includes('x.com')) return 'X (Twitter)';
    if (hostname.includes('instagram')) return 'Instagram';
    if (hostname.includes('linkedin')) return 'LinkedIn';
    if (hostname.includes('youtube')) return 'YouTube';
    if (hostname.includes('tiktok')) return 'TikTok';
    if (hostname.includes('bing')) return 'Bing';
    if (hostname.includes('yahoo')) return 'Yahoo';
    
    return hostname;
  } catch {
    return referrer.substring(0, 100);
  }
};

// Track page view
const trackPageView = async (userId = null, timeOnPage = null) => {
  try {
    const visitorId = getVisitorId();
    const sessionId = getSessionId();
    
    const data = {
      page_url: window.location.pathname + window.location.search,
      page_title: document.title,
      referrer: getReferrerSource(),
      screen_width: window.screen.width,
      screen_height: window.screen.height,
      language: navigator.language || navigator.userLanguage,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      session_id: sessionId,
      visitor_id: visitorId,
      user_id: userId,
      event_type: timeOnPage ? 'page_exit' : 'pageview',
      time_on_page: timeOnPage
    };
    
    // Use sendBeacon for page exits (more reliable)
    if (timeOnPage && navigator.sendBeacon) {
      navigator.sendBeacon(
        `${API_URL}/api/analytics/track`,
        JSON.stringify(data)
      );
    } else {
      await fetch(`${API_URL}/api/analytics/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
    }
  } catch (error) {
    // Silent fail - don't break the site for analytics
    console.debug('Analytics tracking failed:', error);
  }
};

// React hook for visitor tracking
export const useVisitorTracking = (userId = null) => {
  const pageLoadTime = useRef(Date.now());
  const currentPath = useRef(window.location.pathname);
  
  // Track time on page when leaving
  const trackTimeOnPage = useCallback(() => {
    const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
    trackPageView(userId, timeSpent);
  }, [userId]);
  
  useEffect(() => {
    // Track initial page view
    trackPageView(userId);
    pageLoadTime.current = Date.now();
    
    // Track when user leaves/closes page
    const handleBeforeUnload = () => {
      trackTimeOnPage();
    };
    
    // Track visibility changes (tab switching)
    const handleVisibilityChange = () => {
      if (document.hidden) {
        trackTimeOnPage();
      } else {
        pageLoadTime.current = Date.now();
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userId, trackTimeOnPage]);
  
  // Track route changes (for SPAs)
  useEffect(() => {
    const handleRouteChange = () => {
      if (window.location.pathname !== currentPath.current) {
        // Track time on previous page
        const timeSpent = Math.round((Date.now() - pageLoadTime.current) / 1000);
        trackPageView(userId, timeSpent);
        
        // Reset for new page
        currentPath.current = window.location.pathname;
        pageLoadTime.current = Date.now();
        
        // Track new page view
        setTimeout(() => trackPageView(userId), 100);
      }
    };
    
    // Listen for popstate (back/forward buttons)
    window.addEventListener('popstate', handleRouteChange);
    
    // Create observer for URL changes (pushState/replaceState)
    const originalPushState = window.history.pushState;
    const originalReplaceState = window.history.replaceState;
    
    window.history.pushState = function(...args) {
      originalPushState.apply(this, args);
      handleRouteChange();
    };
    
    window.history.replaceState = function(...args) {
      originalReplaceState.apply(this, args);
      handleRouteChange();
    };
    
    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      window.history.pushState = originalPushState;
      window.history.replaceState = originalReplaceState;
    };
  }, [userId]);
};

// Export utility functions
export { getVisitorId, getSessionId, trackPageView };

export default useVisitorTracking;
